<template>
  <section
    v-if="props.highlights.length"
    class="highlights-section surface-panel surface-panel--elevated">
    <div class="container">
      <h2 class="highlights-section__title">{{ props.title }}</h2>
      <ul class="highlights-section__grid">
        <li
          v-for="highlight in props.highlights"
          :key="highlight.Id"
          class="highlights-section__card">
          <div class="highlights-section__card-header">
            <span
              class="highlights-section__emoji"
              aria-hidden="true">
              {{ highlight.Emoji }}
            </span>
            <h3 class="highlights-section__card-title">{{ highlight.Title }}</h3>
          </div>
          <p
            v-if="highlight.Content"
            class="highlights-section__card-text">
            {{ highlight.Content }}
          </p>
        </li>
      </ul>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import type { PrePeersHighlightsSectionProps } from '~/types/PrePeersHighlightsSection'
import type { DifferentiatorObj } from '~/types/school'

const props: PrePeersHighlightsSectionProps = defineProps({
  title: { type: String, required: true },
  highlights: { type: Array as PropType<DifferentiatorObj[]>, required: true },
})
</script>

<style scoped>
.highlights-section__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-8);
  text-align: left;
}

.highlights-section__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
  margin: 0;
  padding: 0;
  list-style: none;
}

.highlights-section__card {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.highlights-section__card-header {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.highlights-section__emoji {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  font-size: 1.375rem;
  line-height: 1;
  background-color: var(--bg-button);
  border-radius: var(--radius-full);
}

.highlights-section__card-title {
  font-size: var(--text-base);
  font-weight: 700;
  line-height: var(--leading-snug);
  color: var(--text-primary);
}

.highlights-section__card-text {
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

@media (max-width: 768px) {
  .highlights-section__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-6);
  }

  .highlights-section__grid {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-3);
  }

  .highlights-section__card {
    gap: var(--space-3);
    padding: var(--space-5);
  }
}

@media (max-width: 640px) {
  .highlights-section__title {
    font-size: var(--text-lg);
  }

  .highlights-section__emoji {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
}
</style>
